import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import PropTypes from "prop-types";
import reactStringReplace from "react-string-replace";
import {Icon, IconButton, makeStyles, Tooltip} from "@material-ui/core";
import {Draggable} from "react-beautiful-dnd";

import FieldScreenComponent from "../FieldScreenComponent";

import useConfirm from "../../../Utils/Confirmation/useConfirm";
import request from "../../../../../Services/API/Requests";
import {multiLanguageValue} from "../../../Utils/lib";
import {findParams} from "../../../Utils";

const useStyles = makeStyles(() => ({
    restrictionItemContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "3px",
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        '&:focus-within': {
            '& $removeButtonContainer': {
                width: "auto",
                height: "auto",
                opacity: 1,
            }
        },
        '&:hover': {
            '& $removeButtonContainer': {
                width: "auto",
                height: "auto",
                opacity: 1,
            }
        }
    },
    removeButtonContainer: {
        display: "flex",
        gap: '5px',
        width: 0,
        height: 0,
        opacity: 0,
        overflow: 'hidden'
    },
    restrictionItem: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },
    icon: {
        padding: 0
    }
}))

const RestrictionItem = ({restriction, sourcePath, index, addGroup, removeGroup, isCreating}) => {
    const classes = useStyles();
    const userLanguage = useSelector(state => state.app.userLanguage);
    const [restrictionRecord, setRestrictionRecord] = useState({});
    const [isHaveParams, setIsHaveParams] = useState(false);
    const itemRef = useRef(null);
    const {confirm} = useConfirm();

    useEffect(() => {
        if (restriction.id) {
            request({
                url: "/restriction/read",
                data: {
                    id: restriction.id,
                }
            }).then(res => {
                setRestrictionRecord(res);
                setIsHaveParams(findParams(multiLanguageValue(res.template, userLanguage)).params.length > 0)
            }).catch(error => {
                console.log(error)
            })
        }
    }, [restriction.id, userLanguage]);


    useEffect(() => {
        if (itemRef.current && (!isHaveParams && !isCreating)) {
            itemRef.current.focus();
            itemRef.current.setAttribute("tabIndex", "-1");
        }
    }, [isCreating, isHaveParams, itemRef.current])

    function removeItem(path) {
        confirm({
            submitFn: () => {
                removeGroup(path)
            }
        })
    }

    return (
        <Draggable draggableId={restriction.id + "_" + index} index={index} key={index}>
            {(provided) => (
                <div className={classes.restrictionItemContainer}
                     ref={provided.innerRef}
                     {...provided.draggableProps}
                     {...provided.dragHandleProps}
                     tabIndex={-1}
                >
                    <div className={classes.restrictionItem} ref={itemRef} tabIndex={isHaveParams ? -1 : 0}>
                        {reactStringReplace(multiLanguageValue(restrictionRecord.template, userLanguage), /{(.*?)}/g, (match, i) => {
                            const field = restrictionRecord.variable.find(field => field.name === match);
                            return (
                                <div style={{padding: '0 6px'}} key={i}>
                                    <FieldScreenComponent
                                        field={{
                                            properties: {
                                                ...field,
                                                label: "",
                                                disabled: isCreating,
                                                required: field.required,
                                                autofocus: !isCreating && (i === 1),
                                                path: `${sourcePath}.${index}.parameters.${field.name}`,
                                            },
                                            styles: [{width: '150px'}, {}]
                                        }}
                                    />
                                </div>
                            )
                        })}
                    </div>

                    <div className={classes.removeButtonContainer}>
                        <IconButton className={classes.icon}
                                    disabled={isCreating}
                                    onClick={() => {
                                        addGroup(sourcePath, index + 1)
                                    }}
                        >
                            <Tooltip title={'Create new'}>
                                <Icon fontSize={"small"}>add</Icon>
                            </Tooltip>
                        </IconButton>

                        <IconButton className={classes.icon}
                                    onClick={() => {
                                        removeItem(sourcePath + "." + index)
                                    }}
                        >
                            <Tooltip title={"Delete"}>
                                <Icon fontSize={"small"}>highlight_off</Icon>
                            </Tooltip>
                        </IconButton>
                    </div>
                </div>
            )}
        </Draggable>
    )
}
export default RestrictionItem

RestrictionItem.propTypes = {
    sourcePath: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    addGroup: PropTypes.func,
    removeGroup: PropTypes.func,
    restriction: PropTypes.shape({
        id: PropTypes.string.isRequired,
        parameter: PropTypes.any
    })
}